import React, { useEffect, useState } from "react";
import api from "../../utils/axiosInstance";
import { FaCloud, FaCheckCircle, FaTimesCircle, FaDatabase } from "react-icons/fa";
import { useModal } from "../../context/ModalContext";

export default function StorageTab() {
  const modal = useModal();
  const [userSettings, setUserSettings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [form, setForm] = useState({
    provider: "r2",
    accountId: "",
    accessKeyId: "",
    secretAccessKey: "",
    bucketName: "",
    region: "auto",
    publicUrl: "",
    enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("/api/settings/storage")
      .then((res) => {
        if (res.data.data) {
          setUserSettings(res.data.data);
          setForm((prev) => ({ ...prev, ...res.data.data }));
          setShowForm(true);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setTestResult(null);
    try {
      const res = await api.put("/api/settings/storage", form);
      setUserSettings(res.data.data);
      setTestResult({ success: true, message: "Storage settings saved successfully." });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.error || "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post("/api/settings/storage/test");
      setTestResult({ success: true, message: res.data.message });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.error || "Connection test failed" });
    } finally {
      setTesting(false);
    }
  };

  const handleRemove = async () => {
    if (!(await modal.confirm("Remove your storage override? Your media will use the platform default storage.", { danger: true, confirmText: "Remove" }))) return;
    try {
      await api.delete("/api/settings/storage");
      setUserSettings(null);
      setShowForm(false);
      setForm({
        provider: "r2", accountId: "", accessKeyId: "", secretAccessKey: "",
        bucketName: "", region: "auto", publicUrl: "", enabled: true,
      });
      setTestResult(null);
    } catch (err) {
      modal.alert("Failed to remove storage settings.", "error");
    }
  };

  if (loading) {
    return <div className="text-sm text-ink-muted p-4">Loading...</div>;
  }

  const usingOwnBucket = !!userSettings;
  const providerLabel = form.provider === "s3" ? "Amazon S3" : "Cloudflare R2";

  return (
    <div className="max-w-5xl">
      <h2 className="text-lg font-semibold text-ink-primary mb-1">Cloud Storage</h2>
      <p className="text-sm text-ink-muted mb-5">
        All your WhatsApp media (images, videos, documents, audio) is stored securely.
        You can optionally connect your own cloud bucket for full data ownership.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Left: Storage Status sidebar */}
        <aside className="space-y-4">
          <div className="bg-surface-card border border-surface-border rounded-xl p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted mb-3">
              Storage Status
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${usingOwnBucket ? "bg-orange-50" : "bg-green-50"}`}>
                {usingOwnBucket ? (
                  <FaDatabase className="text-orange-500" size={16} />
                ) : (
                  <FaCheckCircle className="text-green-500" size={16} />
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-ink-primary truncate">
                  {usingOwnBucket ? providerLabel : "Platform Storage"}
                </div>
                <div className="text-xs text-ink-muted">
                  {usingOwnBucket ? "Your own bucket" : "Managed by Walytic"}
                </div>
              </div>
            </div>
            <div className="space-y-2 pt-3 border-t border-surface-border">
              <div className="flex items-center justify-between text-xs">
                <span className="text-ink-muted">Connection</span>
                <span className={`font-medium ${usingOwnBucket ? "text-orange-600" : "text-green-600"}`}>
                  {usingOwnBucket ? "Custom" : "Healthy"}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-ink-muted">Provider</span>
                <span className="font-medium text-ink-primary">
                  {usingOwnBucket ? providerLabel : "Walytic Cloud"}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-ink-muted">Data ownership</span>
                <span className="font-medium text-ink-primary">
                  {usingOwnBucket ? "You" : "Walytic"}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-surface-card border border-surface-border rounded-xl p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2">
              Supported Providers
            </div>
            <ul className="space-y-1.5 text-xs text-ink-muted">
              <li className="flex items-center gap-2">
                <FaCloud className="text-brand" size={11} /> Cloudflare R2
              </li>
              <li className="flex items-center gap-2">
                <FaDatabase className="text-orange-500" size={11} /> Amazon S3
              </li>
            </ul>
          </div>
        </aside>

        {/* Right: existing config form / status */}
        <div>
      {/* Current status card */}
      {!showForm && (
        <div className="bg-surface-card border border-surface-border rounded-xl p-5 mb-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
              <FaCheckCircle className="text-green-500" size={16} />
            </div>
            <div>
              <div className="text-sm font-semibold text-ink-primary">Using Platform Storage</div>
              <div className="text-xs text-ink-muted">Your media is stored on Walytic's secure cloud infrastructure.</div>
            </div>
          </div>

          <button
            className="btn-outline btn-sm mt-2"
            onClick={() => setShowChoiceModal(true)}
          >
            Configure Storage
          </button>
        </div>
      )}

      {/* Choice Modal */}
      {showChoiceModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold text-ink-primary mb-2">Where should we store your media?</h3>
            <p className="text-sm text-ink-muted mb-5">
              Choose how your WhatsApp images, videos, and documents are stored.
            </p>

            <div className="grid grid-cols-1 gap-3">
              {/* Option 1: Platform default */}
              <button
                onClick={() => setShowChoiceModal(false)}
                className="flex items-start gap-4 p-4 rounded-xl border-2 border-surface-border hover:border-brand/40 hover:bg-brand-50/30 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FaCloud className="text-brand" size={18} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-ink-primary">Store with Walytic</div>
                  <div className="text-xs text-ink-muted mt-0.5">
                    We handle everything. Your media is stored securely on our platform.
                    No setup needed.
                  </div>
                  <span className="inline-block mt-2 text-xs font-medium text-brand bg-brand-50 px-2 py-0.5 rounded-full">
                    Recommended
                  </span>
                </div>
              </button>

              {/* Option 2: Own bucket */}
              <button
                onClick={() => {
                  setShowChoiceModal(false);
                  setShowForm(true);
                }}
                className="flex items-start gap-4 p-4 rounded-xl border-2 border-surface-border hover:border-brand/40 hover:bg-brand-50/30 transition-all text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <FaDatabase className="text-orange-500" size={18} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-ink-primary">Use My Own S3 / R2 Bucket</div>
                  <div className="text-xs text-ink-muted mt-0.5">
                    Connect your own Cloudflare R2 or Amazon S3 bucket. You retain full
                    ownership and control of your data.
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowChoiceModal(false)}
              className="w-full text-center text-sm text-ink-muted hover:text-ink-primary mt-4 py-2"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Own bucket form */}
      {showForm && (
        <div className="bg-surface-card border border-surface-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FaDatabase className="text-orange-500" size={14} />
              <span className="text-sm font-semibold text-ink-primary">
                {userSettings ? "Your Cloud Bucket" : "Connect Your Bucket"}
              </span>
            </div>
            {userSettings && (
              <button
                onClick={handleRemove}
                className="text-xs text-red-500 hover:underline"
              >
                Remove & use platform default
              </button>
            )}
          </div>

          <form onSubmit={handleSave} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="input-group">
                <label className="input-label">Provider</label>
                <select
                  className="input text-sm"
                  value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.target.value })}
                >
                  <option value="r2">Cloudflare R2</option>
                  <option value="s3">Amazon S3</option>
                </select>
              </div>

              <div className="input-group">
                <label className="input-label">Region</label>
                <input
                  className="input text-sm"
                  value={form.region}
                  onChange={(e) => setForm({ ...form, region: e.target.value })}
                  placeholder="auto"
                />
              </div>
            </div>

            {form.provider === "r2" && (
              <div className="input-group">
                <label className="input-label">Cloudflare Account ID</label>
                <input
                  className="input text-sm"
                  value={form.accountId}
                  onChange={(e) => setForm({ ...form, accountId: e.target.value })}
                  placeholder="Found in your Cloudflare dashboard URL"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="input-group">
                <label className="input-label">Access Key ID</label>
                <input
                  className="input text-sm"
                  value={form.accessKeyId}
                  onChange={(e) => setForm({ ...form, accessKeyId: e.target.value })}
                  placeholder="Access Key ID"
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">Secret Access Key</label>
                <input
                  className="input text-sm"
                  type="password"
                  value={form.secretAccessKey}
                  onChange={(e) => setForm({ ...form, secretAccessKey: e.target.value })}
                  placeholder={userSettings ? "Leave blank to keep current" : "Secret Access Key"}
                  required={!userSettings}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="input-group">
                <label className="input-label">Bucket Name</label>
                <input
                  className="input text-sm"
                  value={form.bucketName}
                  onChange={(e) => setForm({ ...form, bucketName: e.target.value })}
                  placeholder="my-media-bucket"
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">Public URL (optional)</label>
                <input
                  className="input text-sm"
                  value={form.publicUrl}
                  onChange={(e) => setForm({ ...form, publicUrl: e.target.value })}
                  placeholder="https://media.yourdomain.com"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <button className="btn-primary btn-sm" type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Settings"}
              </button>
              <button
                type="button"
                className="btn-outline btn-sm"
                onClick={handleTest}
                disabled={testing || !userSettings}
              >
                {testing ? "Testing..." : "Test Connection"}
              </button>
              {!userSettings && (
                <button
                  type="button"
                  className="text-xs text-ink-muted hover:text-ink-primary"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
              )}
            </div>

            {testResult && (
              <div className={`flex items-center gap-2 text-sm p-3 rounded-lg border ${
                testResult.success
                  ? "bg-green-50 text-green-700 border-green-200"
                  : "bg-red-50 text-red-700 border-red-200"
              }`}>
                {testResult.success ? <FaCheckCircle size={14} /> : <FaTimesCircle size={14} />}
                {testResult.message}
              </div>
            )}
          </form>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
